import React, { useState } from 'react';
import { Box, Typography, Button, Collapse } from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  BuildCircle as ToolIcon,
  LockOutlined as AuthIcon,
  WifiOff as NetworkIcon,
  Refresh as RetryIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Inline error message component for chat.
 * Renders type-specific error bubbles with action buttons.
 *
 * Error types:
 * - ai_failure: Red, retry button
 * - tool_failure: Orange, retry + expandable details
 * - auth_expiry: Blue, sign in button
 * - network_error: Grey, retry button
 */
const ERROR_CONFIG = {
  ai_failure: {
    icon: ErrorIcon,
    color: '#d32f2f',
    bgColor: '#fdecea',
    label: 'AI Error',
    showRetry: true,
    showDetails: false,
  },
  tool_failure: {
    icon: ToolIcon,
    color: '#ed6c02',
    bgColor: '#fff4e5',
    label: 'Tool Error',
    showRetry: true,
    showDetails: true,
  },
  auth_expiry: {
    icon: AuthIcon,
    color: '#0288d1',
    bgColor: '#e3f2fd',
    label: 'Session Expired',
    showRetry: false,
    showDetails: false,
  },
  network_error: {
    icon: NetworkIcon,
    color: '#757575',
    bgColor: '#f5f5f5',
    label: 'Network Error',
    showRetry: true,
    showDetails: false,
  },
};

// eslint-disable-next-line max-lines-per-function
const ChatErrorMessage = ({
  errorType = 'ai_failure',
  message,
  toolName,
  details,
  onRetry,
  timestamp
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const { login } = useAuth();

  const config = ERROR_CONFIG[errorType] || ERROR_CONFIG.ai_failure;
  const Icon = config.icon;

  const handleSignIn = async () => {
    try {
      await login();
    } catch (err) {
      console.error('Re-auth failed:', err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '85%',
        ml: 1,
        mr: 'auto',
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          backgroundColor: config.bgColor,
          borderLeft: `4px solid ${config.color}`,
        }}
      >
        <Icon sx={{ color: config.color, mt: 0.25 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: config.color,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {config.label}
            {toolName && `: ${toolName}`}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: 'text.primary', mt: 0.5 }}
          >
            {message}
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            {config.showRetry && onRetry && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RetryIcon />}
                onClick={onRetry}
                sx={{
                  borderColor: config.color,
                  color: config.color,
                  '&:hover': {
                    borderColor: config.color,
                    backgroundColor: `${config.color}10`,
                  },
                }}
              >
                Retry
              </Button>
            )}

            {errorType === 'auth_expiry' && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AuthIcon />}
                onClick={handleSignIn}
                sx={{
                  backgroundColor: config.color,
                  '&:hover': {
                    backgroundColor: `${config.color}dd`,
                  },
                }}
              >
                Sign In
              </Button>
            )}

            {config.showDetails && details && (
              <Button
                size="small"
                variant="text"
                endIcon={showDetails ? <CollapseIcon /> : <ExpandIcon />}
                onClick={() => setShowDetails(!showDetails)}
                sx={{ color: 'text.secondary' }}
              >
                {showDetails ? 'Hide' : 'Show'} Details
              </Button>
            )}
          </Box>

          {/* Expandable technical details */}
          <Collapse in={showDetails}>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.04)',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: 'text.secondary',
              }}
            >
              {details}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {timestamp && (
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', mt: 0.5, ml: 1 }}
        >
          {new Date(timestamp).toLocaleTimeString()}
        </Typography>
      )}
    </Box>
  );
};

export default ChatErrorMessage;
